/** biome-ignore-all lint/suspicious/useIterableCallbackReturn: biome ignore */
'use client'

import { Disclosure, DisclosureButton, DisclosurePanel, Popover, PopoverPanel } from '@headlessui/react'
import classNames from 'classnames'
import cn from 'classnames'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import type { Content } from '@/content-types'
import { isFilled, type LinkField } from '@/lib/content-fields'
import { ContentImage, ContentLink } from '@/lib/rich-text'
import { linkResolver } from '@/utils/linkResolver'
import { IconComponent } from '../Icon/Icon'

export interface NavigationbarProps {
  layout: Content.LayoutDocument
}

/**
 * Floating pill navigation. It sits on top of any background, dark or light, and
 * tightens up slightly once the page is scrolled.
 */
export const Navigationbar = ({ layout }: NavigationbarProps) => {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const isActive = (link: LinkField, items?: { link: LinkField }[]) => {
    if (!isFilled.link(link)) return false
    if (items) {
      return items.some((item) => isFilled.link(item.link) && item.link.url === pathname)
    }
    return false
  }

  return (
    <Disclosure as="nav" className="relative m-0">
      <div
        className={cn('fixed inset-x-0 z-50 px-3 transition-all duration-300', scrolled ? 'top-2' : 'top-4')}
      >
        <div
          className={cn(
            'mx-auto flex max-w-[1180px] items-center justify-between rounded-full bg-white/92 py-2 pr-2 pl-5 ring-1 ring-black/5 backdrop-blur-xl transition-shadow duration-300',
            scrolled
              ? 'shadow-[0_12px_40px_-16px_rgba(18,6,22,0.45)]'
              : 'shadow-[0_8px_30px_-18px_rgba(18,6,22,0.35)]'
          )}
        >
          <Link href="/" className="flex shrink-0 items-center">
            <ContentImage field={layout.data.logo} alt="Signital" className="h-[30px] w-auto" />
          </Link>

          <ul className="hidden items-center gap-x-1 lg:flex">
            {layout.data.slices
              .filter(
                (slice) =>
                  slice.primary.link.text !== 'Contact' && slice.primary.link.text !== 'About Signital'
              )
              .map((slice) => {
                if (slice.variation === 'default') {
                  return (
                    <NavbarItem
                      key={slice.id}
                      slice={slice.primary.link}
                      active={isActive(slice.primary.link)}
                    />
                  )
                } else if (slice.variation === 'withSubmenu') {
                  return (
                    <NavbarItemWithSubmenu
                      key={slice.id}
                      slice={slice}
                      active={isActive(slice.primary.link, slice.primary.items)}
                      pathname={pathname}
                    />
                  )
                }
              })}
          </ul>

          <div className="flex items-center gap-x-1">
            <div className="hidden items-center gap-x-1 lg:flex">
              <ContentLink
                field={layout.data.primary_button}
                className="hover:text-primary rounded-full px-4 py-2.5 text-[14px] font-semibold text-[#2f2f2f] transition"
              />
              {isFilled.link(layout.data.demo_button) && (
                <ContentLink
                  field={layout.data.demo_button}
                  className="hover:border-primary hover:text-primary rounded-full border border-black/10 px-4 py-2.5 text-[14px] font-semibold whitespace-nowrap text-[#2f2f2f] transition"
                />
              )}
              <ContentLink
                field={layout.data.secondary_button}
                className="bg-ink hover:bg-primary rounded-full px-5 py-2.5 text-[14px] font-semibold whitespace-nowrap text-white transition duration-200 ease-in-out"
              />
            </div>

            {/* The trial CTA stays in the bar on phones, next to the menu button, so
                a paid-traffic visitor never has to open the menu to find it. */}
            <ContentLink
              field={layout.data.secondary_button}
              className="bg-ink hover:bg-primary rounded-full px-4 py-2 text-[13px] font-semibold whitespace-nowrap text-white transition duration-200 ease-in-out lg:hidden"
            >
              {layout.data.secondary_button_short ?? layout.data.secondary_button.text}
            </ContentLink>

            <DisclosureButton className="inline-flex size-11 items-center justify-center rounded-full text-black hover:bg-black/5 focus:outline-hidden lg:hidden">
              <span className="sr-only">Open main menu</span>
              <IconComponent icon="MobileMenu" size="medium" className="size-6" />
            </DisclosureButton>
          </div>
        </div>
      </div>

      <DisclosurePanel className="bg-ink fixed inset-0 z-[999] h-screen text-white lg:hidden">
        <div className="container flex h-[80px] items-center justify-between">
          <ContentImage
            field={layout.data.logo}
            alt="Signital"
            className="h-[30px] w-auto brightness-0 invert"
          />
          <DisclosureButton className="inline-flex size-11 items-center justify-center rounded-full text-white hover:bg-white/10 focus:outline-hidden">
            <span className="sr-only">Close main menu</span>
            <IconComponent icon="XMark" size="medium" aria-hidden="true" className="block size-6" />
          </DisclosureButton>
        </div>

        <ul className="space-y-1 px-8 pt-8">
          {layout.data.slices.map((slice) => {
            if (slice.variation === 'default') {
              return (
                <NavbarItemMobile
                  key={slice.id}
                  slice={slice.primary.link}
                  active={isActive(slice.primary.link)}
                />
              )
            } else if (slice.variation === 'withSubmenu') {
              return (
                <NavbarItemWithSubmenuMobile
                  key={slice.id}
                  slice={slice}
                  active={isActive(slice.primary.link, slice.primary.items)}
                />
              )
            }
          })}
        </ul>

        <div className="mt-8 flex flex-col gap-3 px-8">
          <ContentLink
            field={layout.data.secondary_button}
            className="bg-primary hover:bg-signal w-full rounded-full px-7 py-3.5 text-center text-lg font-semibold text-white transition"
          />
          {isFilled.link(layout.data.demo_button) && (
            <ContentLink
              field={layout.data.demo_button}
              className="w-full rounded-full border border-white/20 px-7 py-3.5 text-center text-lg font-semibold text-white transition hover:bg-white/10"
            />
          )}
          <ContentLink
            field={layout.data.primary_button}
            className="w-full py-2 text-center text-base font-medium text-white/70 transition hover:text-white"
          />
        </div>
      </DisclosurePanel>
    </Disclosure>
  )
}

const navLinkClass =
  'rounded-full px-4 py-2.5 text-[14px] font-semibold text-[#2f2f2f] transition hover:bg-black/5 hover:text-primary'

const NavbarItem = ({ slice, active }: { slice: LinkField; active: boolean }) => (
  <li>
    <ContentLink field={slice} className={cn(navLinkClass, { 'text-primary': active })} />
  </li>
)

const NavbarItemMobile = ({ slice, active }: { slice: LinkField; active: boolean }) => (
  <li className="border-b border-white/10 py-4">
    <ContentLink
      field={slice}
      className={cn('font-rubik text-3xl font-medium', { 'text-signal': active, 'text-white': !active })}
    />
  </li>
)

const NavbarItemWithSubmenu = ({
  slice,
  active,
  pathname,
}: {
  slice: Content.MenuItemSliceWithSubmenu
  active: boolean
  pathname: string
}) => {
  return (
    <li className="group relative">
      <Popover className="relative">
        {({ open }) => (
          <>
            <div
              className={cn(navLinkClass, 'inline-flex items-center gap-x-1.5 outline-none', {
                'text-primary': active,
              })}
            >
              {slice.primary.link.text === 'Resources' ? (
                <span>{slice.primary.link.text}</span>
              ) : (
                <ContentLink field={slice.primary.link} linkResolver={linkResolver} />
              )}
              <IconComponent
                icon="ChevronDown"
                aria-hidden="true"
                size="xsmall"
                className="transition group-hover:rotate-180"
              />
            </div>

            <PopoverPanel
              static
              className={classNames(
                'absolute top-full left-1/2 z-10 -translate-x-1/2 pt-3',
                'transform transition-all duration-200 ease-in-out',
                'invisible translate-y-1 opacity-0',
                'group-hover:visible group-hover:translate-y-0 group-hover:opacity-100',
                open ? 'visible opacity-100' : ''
              )}
            >
              <div className="w-60 rounded-2xl bg-white p-2 text-[15px] text-black shadow-[0_24px_60px_-20px_rgba(18,6,22,0.45)] ring-1 ring-black/5">
                {slice.primary.items.map((item) => {
                  if (!isFilled.link(item.link)) return null
                  return (
                    <ContentLink
                      field={item.link}
                      key={item.link.text}
                      linkResolver={linkResolver}
                      className={cn(
                        'hover:text-primary block rounded-xl px-4 py-2.5 font-medium transition-colors duration-150 hover:bg-black/5',
                        { 'text-primary': pathname === item.link.url }
                      )}
                    />
                  )
                })}
              </div>
            </PopoverPanel>
          </>
        )}
      </Popover>
    </li>
  )
}

const NavbarItemWithSubmenuMobile = ({
  slice,
  active,
}: {
  slice: Content.MenuItemSliceWithSubmenu & { id: string }
  active: boolean
}) => {
  const [expand, setExpand] = useState(false)
  return (
    <li className="border-b border-white/10 py-4">
      <div className="flex w-full items-center justify-between">
        <ContentLink
          field={slice.primary.link}
          className={classNames('font-rubik text-3xl font-medium', active ? 'text-signal' : 'text-white')}
        />
        <button
          type="button"
          aria-label="Toggle submenu"
          className="flex size-10 items-center justify-center rounded-full text-white hover:bg-white/10"
          onClick={(e) => {
            e.preventDefault()
            setExpand((prev) => !prev)
          }}
        >
          <IconComponent
            icon="ChevronDown"
            aria-hidden="true"
            size="base"
            className={classNames('transition-transform duration-200', { 'rotate-180': expand })}
          />
        </button>
      </div>

      {expand && (
        <ul className="mt-3 flex flex-col gap-1 pl-1">
          {slice.primary.items.map((item) => (
            <li key={`${slice.id}-link-${item.link.text}`}>
              <ContentLink field={item.link} className="block py-2 text-lg text-white/80 hover:text-white">
                {item.link.text}
              </ContentLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
export default Navigationbar
